/**
 * AlertSound — single shared audio player. Source URL comes from
 * /api/settings/sound (admin-uploaded data URL) or, if missing, a tiny
 * synthesized chime via the Web Audio API so the UI is never silent.
 */
import { api } from "@/lib/api";

let cachedDataUrl = null;
let cachedAt = 0;

async function loadDataUrl() {
  // Refresh every 5 minutes — admin may have changed it
  if (cachedDataUrl !== null && Date.now() - cachedAt < 5 * 60 * 1000) return cachedDataUrl;
  try {
    const { data } = await api.get("/settings/sound");
    cachedDataUrl = data?.data_url || "";
    cachedAt = Date.now();
  } catch (_) {
    cachedDataUrl = "";
    cachedAt = Date.now();
  }
  return cachedDataUrl;
}

function playSynthChime() {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.connect(g); g.connect(ctx.destination);
    o.type = "sine";
    o.frequency.value = 880;
    g.gain.value = 0.0001;
    o.start();
    g.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + 0.02);
    o.frequency.exponentialRampToValueAtTime(440, ctx.currentTime + 0.18);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.45);
    o.stop(ctx.currentTime + 0.5);
    setTimeout(() => ctx.close().catch(() => {}), 600);
  } catch (_) { /* silent */ }
}

export async function playAlertSound() {
  const url = await loadDataUrl();
  if (!url) { playSynthChime(); return; }
  try {
    const audio = new Audio(url);
    audio.volume = 0.85;
    await audio.play();
  } catch (_) {
    // Browsers often block autoplay until first user gesture — fall back to synth
    playSynthChime();
  }
}

/** Force-refresh the cached sound (after admin uploads a new one). */
export function invalidateAlertSoundCache() {
  cachedDataUrl = null;
  cachedAt = 0;
}
